// Warm the browser HTTP cache with the head of upcoming feed videos so the
// <video> element can start playback immediately when the user reaches it.
// Skips on Save-Data and slow connections; deduplicates by URL.

const PREFETCH_BYTES = 1024 * 1024; // ~1 MB — enough for moov + first frames
const SOFT_TIMEOUT_MS = 6000;

const inflight = new Map(); // url -> AbortController
const done = new Set();

const isSlowConnection = () => {
  const conn =
    typeof navigator !== "undefined" &&
    (navigator.connection ||
      navigator.mozConnection ||
      navigator.webkitConnection);
  if (!conn) return false;
  if (conn.saveData) return true;
  const t = conn.effectiveType;
  return t === "slow-2g" || t === "2g" || t === "3g";
};

const schedule = (fn) => {
  if (typeof window === "undefined") return;
  if (typeof window.requestIdleCallback === "function") {
    window.requestIdleCallback(fn, { timeout: 1500 });
  } else {
    setTimeout(fn, 200);
  }
};

const fetchHead = (url) => {
  const ctrl = new AbortController();
  inflight.set(url, ctrl);

  const softTimeout = setTimeout(() => ctrl.abort(), SOFT_TIMEOUT_MS);

  fetch(url, {
    signal: ctrl.signal,
    credentials: "omit",
    mode: "cors",
    // Chrome priority hint — ignored elsewhere
    priority: "low",
    headers: { Range: `bytes=0-${PREFETCH_BYTES - 1}` },
  })
    .then(async (res) => {
      if (!res.ok && res.status !== 206) {
        throw new Error(`status ${res.status}`);
      }
      const reader = res.body && res.body.getReader();
      if (!reader) return;
      // Drain the body so the browser actually receives + caches the bytes.
      // Bail early if we already pulled enough.
      let received = 0;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done: streamDone, value } = await reader.read();
        if (streamDone) break;
        if (value) received += value.length;
        if (received >= PREFETCH_BYTES) {
          try {
            await reader.cancel();
          } catch (_) {
            /* noop */
          }
          break;
        }
      }
    })
    .catch(() => {
      /* network or aborted — fine */
    })
    .finally(() => {
      clearTimeout(softTimeout);
      inflight.delete(url);
      done.add(url);
    });
};

export const prefetchVideo = (url) => {
  if (!url || typeof url !== "string") return;
  if (done.has(url) || inflight.has(url)) return;
  if (isSlowConnection()) return;
  schedule(() => fetchHead(url));
};

export const prefetchVideos = (urls) => {
  if (!Array.isArray(urls)) return;
  for (const u of urls) prefetchVideo(u);
};

export const cancelPrefetch = (url) => {
  const ctrl = inflight.get(url);
  if (ctrl) {
    try {
      ctrl.abort();
    } catch (_) {
      /* noop */
    }
    inflight.delete(url);
  }
};

export const cancelAllPrefetches = () => {
  for (const [, ctrl] of inflight) {
    try {
      ctrl.abort();
    } catch (_) {
      /* noop */
    }
  }
  inflight.clear();
};
